import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Download, FileText, MapPin, Users, Briefcase, DollarSign, CheckCircle } from 'lucide-react';
import { useTechReport } from '@/hooks/useTechReport';
import { useTechCommissionReport } from '@/hooks/useTechCommissionReport';
import { generateTechCommissionHtml, generateSingleTechCommissionHtml, downloadHtmlReport } from '@/utils/techCommissionHtmlGenerator';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { MetroWithTechs, TechPerformanceMetrics } from '@/types/reports';
import { MetricCard, MetricComparison } from './MetricCard';
import { useComparisonMetrics, ComparisonMetrics } from '@/hooks/useComparisonMetrics';
import { calculateChange } from '@/utils/periodComparison';

interface TechniciansReportProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
  compareEnabled?: boolean;
}

export const TechniciansReport = ({ startDate, endDate, timezone: propTimezone, compareEnabled = false }: TechniciansReportProps) => {
  const { timezone: contextTimezone, formatDate } = useTimezoneUtils();
  const timezone = propTimezone || contextTimezone;

  const { data: metroData, isLoading } = useTechReport(startDate, endDate, timezone);
  const { data: commissionData } = useTechCommissionReport(startDate, endDate, timezone);
  const tax = useCompanyTax();
  const { formatMoney: formatCurrency, locale } = useCompanyLocale();
  const [expandedMetros, setExpandedMetros] = useState<Set<string>>(new Set());
  const [expandedTechs, setExpandedTechs] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const { toast } = useToast();

  const toggleMetro = (metroId: string) => {
    setExpandedMetros(prev => {
      const newSet = new Set(prev);
      if (newSet.has(metroId)) {
        newSet.delete(metroId);
      } else {
        newSet.add(metroId);
      }
      return newSet;
    });
  };

  const toggleTech = (techId: string) => {
    setExpandedTechs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(techId)) {
        newSet.delete(techId);
      } else {
        newSet.add(techId);
      }
      return newSet;
    });
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const handleDownloadHtml = () => {
    if (!commissionData) {
      toast({
        title: 'No data available',
        description: 'Please wait for the commission report to load',
        variant: 'destructive'
      });
      return;
    }

    const html = generateTechCommissionHtml(commissionData, tax, locale, timezone);
    const filename = `All_Techs_Commission_Report_${formatDate(startDate, 'yyyy-MM-dd')}_to_${formatDate(endDate, 'yyyy-MM-dd')}.html`;
    downloadHtmlReport(html, filename);

    toast({
      title: 'Report downloaded',
      description: 'HTML commission report has been downloaded successfully'
    });
  };

  const handleDownloadSingleTechHtml = (techId: string) => {
    if (!commissionData) {
      toast({
        title: 'No data available',
        description: 'Please wait for the commission report to load',
        variant: 'destructive'
      });
      return;
    }

    const techData = commissionData.technicians.find(t => t.techId === techId);
    if (!techData) {
      toast({
        title: 'Technician not found',
        variant: 'destructive'
      });
      return;
    }

    const html = generateSingleTechCommissionHtml(
      techData,
      commissionData.startDate,
      commissionData.endDate,
      commissionData.companyName,
      tax,
      locale,
      timezone,
    );
    const filename = `${techData.techName.replace(/\s+/g, '_')}_Commission_Report_${formatDate(startDate, 'yyyy-MM-dd')}_to_${formatDate(endDate, 'yyyy-MM-dd')}.html`;
    downloadHtmlReport(html, filename);

    toast({
      title: 'Report downloaded',
      description: `${techData.techName}'s commission report downloaded successfully`
    });
  };

  // Flatten all technicians for CSV export
  const allTechnicians = useMemo(() => {
    if (!metroData) return [];
    return metroData.flatMap(metro => metro.technicians);
  }, [metroData]);

  // Summary CSV export (technician-level aggregates)
  const handleExportSummaryCSV = () => {
    if (!allTechnicians || allTechnicians.length === 0) return;

    const headers = [
      'Metro',
      'Technician',
      'Total Jobs',
      'Closed Jobs',
      'Canceled Jobs',
      'Closed %',
      'Avg Ticket',
      'Total Parts',
      'Total Tolls',
      'Cash',
      'Card',
      'Bank Transfer',
      'Total Revenue',
    ];

    const rows: (string | number)[][] = [];
    metroData?.forEach(metro => {
      metro.technicians.forEach(tech => {
        rows.push([
          metro.metro_name,
          tech.tech_name,
          tech.total_jobs,
          tech.closed_jobs,
          tech.canceled_jobs,
          formatPercentage(tech.closed_percentage),
          tech.avg_ticket.toFixed(2),
          tech.total_parts_used.toFixed(2),
          tech.total_tolls.toFixed(2),
          tech.cash_payments.toFixed(2),
          tech.card_payments.toFixed(2),
          tech.bank_transfer_payments.toFixed(2),
          tech.total_revenue.toFixed(2),
        ]);
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `technicians-summary-${formatDate(startDate, 'yyyy-MM-dd')}-to-${formatDate(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Jobs CSV export (individual job rows)
  const handleExportJobsCSV = () => {
    if (!metroData || metroData.length === 0) return;

    const escapeField = (field: string | number | null | undefined): string => {
      const str = String(field ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = [
      'Job Number',
      'Customer Name',
      'Technician',
      'Metro',
      'Service Date',
      'Date Posted',
      'Status',
      'Revenue',
      'Parts Cost',
      'Toll Cost',
      'Payment Status',
      'Cancellation Reason',
    ];

    const rows: string[][] = [];

    metroData.forEach(metro => {
      metro.technicians.forEach(tech => {
        // Add completed jobs
        tech.jobs.forEach(job => {
          rows.push([
            escapeField(job.job_number),
            escapeField(job.customer_name),
            escapeField(tech.tech_name),
            escapeField(metro.metro_name),
            escapeField(job.scheduled_date ? formatDate(new Date(job.scheduled_date), 'yyyy-MM-dd') : ''),
            escapeField(job.closed_at ? formatDate(new Date(job.closed_at), 'yyyy-MM-dd') : ''),
            escapeField(job.status),
            escapeField((job.invoice_total || 0).toFixed(2)),
            escapeField((job.total_parts_cost || 0).toFixed(2)),
            escapeField((job.toll_cost || 0).toFixed(2)),
            escapeField(job.payment_status),
            escapeField(''),
          ]);
        });

        // Add cancelled jobs
        tech.cancelled_jobs_details.forEach(job => {
          rows.push([
            escapeField(job.job_number),
            escapeField(job.customer_name),
            escapeField(tech.tech_name),
            escapeField(metro.metro_name),
            escapeField(job.scheduled_date ? formatDate(new Date(job.scheduled_date), 'yyyy-MM-dd') : ''),
            escapeField(job.cancelled_at ? formatDate(new Date(job.cancelled_at), 'yyyy-MM-dd') : ''),
            escapeField(job.status),
            escapeField('0.00'),
            escapeField((job.total_parts_cost || 0).toFixed(2)),
            escapeField((job.toll_cost || 0).toFixed(2)),
            escapeField('n/a'),
            escapeField(job.cancellation_reason || ''),
          ]);
        });
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all-jobs-${formatDate(startDate, 'yyyy-MM-dd')}-to-${formatDate(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Jobs exported',
      description: `${rows.length} job records exported successfully`,
    });
  };

  // Calculate overall totals
  const totals = metroData?.reduce(
    (acc, metro) => ({
      total_jobs: acc.total_jobs + metro.total_jobs,
      closed_jobs: acc.closed_jobs + metro.closed_jobs,
      canceled_jobs: acc.canceled_jobs + metro.canceled_jobs,
      total_revenue: acc.total_revenue + metro.total_revenue,
      total_parts: acc.total_parts + metro.total_parts_used,
      total_tolls: acc.total_tolls + metro.total_tolls,
      cash: acc.cash + metro.cash_payments,
      card: acc.card + metro.card_payments,
      bank: acc.bank + metro.bank_transfer_payments,
    }),
    {
      total_jobs: 0,
      closed_jobs: 0,
      canceled_jobs: 0,
      total_revenue: 0,
      total_parts: 0,
      total_tolls: 0,
      cash: 0,
      card: 0,
      bank: 0,
    }
  );

  const totalClosedPercentage = totals && totals.total_jobs > 0
    ? (totals.closed_jobs / totals.total_jobs) * 100
    : 0;

  const totalAvgTicket = totals && totals.closed_jobs > 0
    ? totals.total_revenue / totals.closed_jobs
    : 0;

  // Comparison data (deferred: only when enabled and main data loaded)
  const { data: comparisonData, isLoading: comparisonLoading } = useComparisonMetrics(
    startDate,
    endDate,
    timezone,
    !isLoading && !!metroData && compareEnabled
  );

  const getComparison = (
    current: number,
    previousKey: keyof ComparisonMetrics,
    isPositiveGood = true
  ): MetricComparison | undefined => {
    if (!comparisonData || !compareEnabled) return undefined;
    const change = calculateChange(current, comparisonData.metrics[previousKey]);
    return { ...change, label: comparisonData.period.label, isPositiveGood };
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Technicians Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!metroData || metroData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Technicians Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No technician data available for the selected date range.
          </p>
        </CardContent>
      </Card>
    );
  }

  const renderTechnicianRow = (tech: TechPerformanceMetrics, metro: MetroWithTechs) => {
    const isExpanded = expandedTechs.has(tech.tech_id);

    return (
      <Collapsible
        key={tech.tech_id}
        open={isExpanded}
        onOpenChange={() => toggleTech(tech.tech_id)}
      >
        <CollapsibleTrigger asChild>
          <div className="cursor-pointer hover:bg-muted/50 rounded-lg transition-colors ml-6 border-l-2 border-muted">
            {/* Desktop Layout */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 items-center">
              <div className="col-span-2 flex items-center gap-2 font-medium">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                {tech.tech_name}
              </div>
              <div className="col-span-1 text-center">
                {formatPercentage(tech.closed_percentage)}
              </div>
              <div className="col-span-1 text-center">{tech.total_jobs}</div>
              <div className="col-span-1 text-center">{tech.closed_jobs}</div>
              <div className="col-span-1 text-center">{tech.canceled_jobs}</div>
              <div className="col-span-1 text-right">{formatCurrency(tech.avg_ticket)}</div>
              <div className="col-span-1 text-right">{formatCurrency(tech.total_parts_used)}</div>
              <div className="col-span-1 text-right">{formatCurrency(tech.total_tolls)}</div>
              <div className="col-span-1 text-right">{formatCurrency(tech.cash_payments)}</div>
              <div className="col-span-1 text-right">{formatCurrency(tech.card_payments)}</div>
              <div className="col-span-1 text-right font-semibold">
                {formatCurrency(tech.total_revenue)}
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 font-medium">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  {tech.tech_name}
                </div>
                <div className="text-sm font-semibold">
                  {formatCurrency(tech.total_revenue)}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Closed: </span>
                  {formatPercentage(tech.closed_percentage)}
                </div>
                <div>
                  <span className="text-muted-foreground">Jobs: </span>
                  {tech.closed_jobs}/{tech.total_jobs}
                </div>
                <div>
                  <span className="text-muted-foreground">Avg: </span>
                  {formatCurrency(tech.avg_ticket)}
                </div>
              </div>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 pt-2 ml-12 border-l-2 border-muted">
            {/* Download Individual Report Button */}
            <div className="mb-4">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadSingleTechHtml(tech.tech_id);
                }}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <FileText className="h-4 w-4 mr-2" />
                Download {tech.tech_name}'s Commission Report
              </Button>
            </div>

            {/* Payment Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground">Cash</p>
                <p className="font-semibold">{formatCurrency(tech.cash_payments)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Card</p>
                <p className="font-semibold">{formatCurrency(tech.card_payments)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Bank Transfer</p>
                <p className="font-semibold">{formatCurrency(tech.bank_transfer_payments)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Revenue</p>
                <p className="font-semibold text-primary">{formatCurrency(tech.total_revenue)}</p>
              </div>
            </div>

            {/* Override Income Section - Show if tech has any override income */}
            {(() => {
              const techCommissionData = commissionData?.technicians.find(t => t.techId === tech.tech_id);
              if (!techCommissionData || techCommissionData.totalOverrideIncome <= 0) return null;

              return (
                <div className="mb-4 p-3 bg-accent/50 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-primary">
                      Override Income Received
                    </p>
                    <p className="font-bold text-primary">
                      {formatCurrency(techCommissionData.totalOverrideIncome)}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Commission earned from other technicians' completed jobs
                  </p>
                  <div className="space-y-1">
                    {(() => {
                      // Aggregate by source tech
                      const bySourceTech = techCommissionData.overrideIncomeItems.reduce((acc, item) => {
                        if (!acc[item.sourceTechId]) {
                          acc[item.sourceTechId] = { name: item.sourceTechName, total: 0 };
                        }
                        acc[item.sourceTechId].total += item.overrideAmount;
                        return acc;
                      }, {} as Record<string, { name: string; total: number }>);

                      return Object.entries(bySourceTech).map(([techId, data]) => (
                        <div key={techId} className="flex items-center justify-between text-sm py-1 border-t border-border/50">
                          <span className="text-muted-foreground">From {data.name}</span>
                          <span className="font-medium text-primary">
                            {formatCurrency(data.total)}
                          </span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              );
            })()}

            {/* Jobs List */}
            <div>
              <p className="text-sm font-semibold mb-2">
                Completed Jobs ({tech.jobs.length})
              </p>
              {tech.jobs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No completed jobs</p>
              ) : (
                <div className="space-y-2">
                  {tech.jobs.map(job => (
                    <div
                      key={job.id}
                      className="flex items-center justify-between p-2 bg-background border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium">
                            {job.job_number}
                          </span>
                          <Badge
                            variant={
                              job.payment_status === 'paid'
                                ? 'default'
                                : job.payment_status === 'partially_paid'
                                  ? 'secondary'
                                  : 'outline'
                            }
                          >
                            {job.payment_status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <CustomerLink
                          customerId={job.customer_id}
                          name={job.customer_name}
                          className="block text-sm text-muted-foreground"
                        />
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">
                          {formatCurrency(job.invoice_total || 0)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {job.closed_at
                            ? `Posted: ${formatDate(new Date(job.closed_at), 'MMM d, yyyy')}`
                            : 'N/A'}
                        </p>
                        {job.scheduled_date && (
                          <p className="text-[10px] text-muted-foreground/70">
                            Service: {formatDate(new Date(job.scheduled_date), 'MMM d')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary MetricCards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard
          title="Total Jobs"
          value={totals?.total_jobs || 0}
          icon={Briefcase}
          comparison={getComparison(totals?.total_jobs || 0, 'totalJobs', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Completed"
          value={totals?.closed_jobs || 0}
          icon={CheckCircle}
          valueColor="text-success"
          comparison={getComparison(totals?.closed_jobs || 0, 'completedJobs', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Close Rate"
          value={`${totalClosedPercentage.toFixed(1)}%`}
          icon={Users}
        />
        <MetricCard
          title="Revenue"
          value={formatCurrency(totals?.total_revenue || 0)}
          icon={DollarSign}
          valueColor="text-primary"
          comparison={getComparison(totals?.total_revenue || 0, 'totalTurnover', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Avg Ticket"
          value={formatCurrency(totalAvgTicket)}
          icon={DollarSign}
        />
      </div>

    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <CardTitle>Technicians Performance</CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleExportJobsCSV} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export All Jobs
            </Button>
            <Button onClick={handleExportSummaryCSV} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export Summary
            </Button>
            <Button onClick={handleDownloadHtml} variant="default" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              HTML Report
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="overview">Overview & Jobs</TabsTrigger>
            <TabsTrigger value="analysis">Closed vs Cancelled</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-2">
            {/* Header Row - Hidden on mobile, visible on tablet+ */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-sm font-semibold text-muted-foreground border-b">
              <div className="col-span-2">Metro / Technician</div>
              <div className="col-span-1 text-center">Closed %</div>
              <div className="col-span-1 text-center">Total</div>
              <div className="col-span-1 text-center">Closed</div>
              <div className="col-span-1 text-center">Canceled</div>
              <div className="col-span-1 text-right">Avg Ticket</div>
              <div className="col-span-1 text-right">Parts</div>
              <div className="col-span-1 text-right">Tolls</div>
              <div className="col-span-1 text-right">Cash</div>
              <div className="col-span-1 text-right">Card</div>
              <div className="col-span-1 text-right">Revenue</div>
            </div>

            {/* Metro Rows */}
            {metroData.map(metro => {
              const isMetroExpanded = expandedMetros.has(metro.metro_id);

              return (
                <div key={metro.metro_id} className="space-y-1">
                  <Collapsible
                    open={isMetroExpanded}
                    onOpenChange={() => toggleMetro(metro.metro_id)}
                  >
                    <CollapsibleTrigger asChild>
                      <div className="cursor-pointer hover:bg-primary/5 rounded-lg transition-colors bg-muted/30">
                        {/* Desktop Metro Row */}
                        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 items-center">
                          <div className="col-span-2 flex items-center gap-2 font-semibold">
                            {isMetroExpanded ? (
                              <ChevronDown className="h-4 w-4 text-primary" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-primary" />
                            )}
                            <MapPin className="h-4 w-4 text-primary" />
                            {metro.metro_name}
                            <Badge variant="outline" className="ml-2">
                              {metro.technicians.length} tech{metro.technicians.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                          <div className="col-span-1 text-center font-medium">
                            {formatPercentage(metro.closed_percentage)}
                          </div>
                          <div className="col-span-1 text-center font-medium">{metro.total_jobs}</div>
                          <div className="col-span-1 text-center font-medium">{metro.closed_jobs}</div>
                          <div className="col-span-1 text-center font-medium">{metro.canceled_jobs}</div>
                          <div className="col-span-1 text-right font-medium">{formatCurrency(metro.avg_ticket)}</div>
                          <div className="col-span-1 text-right font-medium">{formatCurrency(metro.total_parts_used)}</div>
                          <div className="col-span-1 text-right font-medium">{formatCurrency(metro.total_tolls)}</div>
                          <div className="col-span-1 text-right font-medium">{formatCurrency(metro.cash_payments)}</div>
                          <div className="col-span-1 text-right font-medium">{formatCurrency(metro.card_payments)}</div>
                          <div className="col-span-1 text-right font-bold text-primary">
                            {formatCurrency(metro.total_revenue)}
                          </div>
                        </div>

                        {/* Mobile Metro Row */}
                        <div className="md:hidden px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 font-semibold">
                              {isMetroExpanded ? (
                                <ChevronDown className="h-4 w-4 text-primary" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-primary" />
                              )}
                              <MapPin className="h-4 w-4 text-primary" />
                              {metro.metro_name}
                            </div>
                            <div className="text-sm font-bold text-primary">
                              {formatCurrency(metro.total_revenue)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge variant="outline">
                              {metro.technicians.length} tech{metro.technicians.length !== 1 ? 's' : ''}
                            </Badge>
                            <span>{metro.closed_jobs}/{metro.total_jobs} jobs</span>
                            <span>{formatPercentage(metro.closed_percentage)}</span>
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="space-y-1 mt-1">
                        {metro.technicians.map(tech => renderTechnicianRow(tech, metro))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              );
            })}

            {/* Totals Row */}
            <div className="border-t-2 border-primary/20 mt-4 pt-2">
              <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-muted/50 rounded-lg font-semibold">
                <div className="col-span-2">TOTALS</div>
                <div className="col-span-1 text-center">
                  {formatPercentage(totalClosedPercentage)}
                </div>
                <div className="col-span-1 text-center">{totals?.total_jobs}</div>
                <div className="col-span-1 text-center">{totals?.closed_jobs}</div>
                <div className="col-span-1 text-center">{totals?.canceled_jobs}</div>
                <div className="col-span-1 text-right">{formatCurrency(totalAvgTicket)}</div>
                <div className="col-span-1 text-right">{formatCurrency(totals?.total_parts || 0)}</div>
                <div className="col-span-1 text-right">{formatCurrency(totals?.total_tolls || 0)}</div>
                <div className="col-span-1 text-right">{formatCurrency(totals?.cash || 0)}</div>
                <div className="col-span-1 text-right">{formatCurrency(totals?.card || 0)}</div>
                <div className="col-span-1 text-right text-primary">
                  {formatCurrency(totals?.total_revenue || 0)}
                </div>
              </div>

              <div className="md:hidden px-4 py-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">TOTALS</span>
                  <span className="font-semibold text-primary">
                    {formatCurrency(totals?.total_revenue || 0)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Closed: </span>
                    {formatPercentage(totalClosedPercentage)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Jobs: </span>
                    {totals?.closed_jobs}/{totals?.total_jobs}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Avg: </span>
                    {formatCurrency(totalAvgTicket)}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="analysis" className="space-y-4">
            {/* Overall Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Total Completed</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-success tabular-nums">
                    {totals?.closed_jobs || 0}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(totals?.total_revenue || 0)} revenue
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Total Cancelled</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-destructive">
                    {totals?.canceled_jobs || 0}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {totals?.total_jobs || 0} total jobs
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Cancellation Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatPercentage(
                      totals && totals.total_jobs > 0
                        ? ((totals.canceled_jobs / totals.total_jobs) * 100)
                        : 0
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Across all technicians
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Cancellation Reasons Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cancellation Reasons Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const reasonCounts = new Map<string, number>();
                  metroData?.forEach(metro => {
                    metro.technicians.forEach(tech => {
                      tech.cancelled_jobs_details.forEach(job => {
                        const reason = job.cancellation_reason || 'Not specified';
                        reasonCounts.set(reason, (reasonCounts.get(reason) || 0) + 1);
                      });
                    });
                  });

                  const sortedReasons = Array.from(reasonCounts.entries())
                    .sort((a, b) => b[1] - a[1]);

                  if (sortedReasons.length === 0) {
                    return (
                      <p className="text-muted-foreground text-center py-4">
                        No cancelled jobs in this period
                      </p>
                    );
                  }

                  return (
                    <div className="space-y-2">
                      {sortedReasons.map(([reason, count]) => (
                        <div key={reason} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{reason}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatPercentage(
                                totals?.canceled_jobs ? (count / totals.canceled_jobs) * 100 : 0
                              )} of all cancellations
                            </p>
                          </div>
                          <Badge variant="outline" className="text-base">
                            {count} {count === 1 ? 'job' : 'jobs'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Per-Metro Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Metro Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {metroData?.map(metro => {
                    const cancellationRate = metro.total_jobs > 0
                      ? (metro.canceled_jobs / metro.total_jobs) * 100
                      : 0;

                    return (
                      <Collapsible key={metro.metro_id}>
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-3">
                              <MapPin className="h-4 w-4 text-primary" />
                              <div className="text-left">
                                <p className="font-semibold">{metro.metro_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {metro.closed_jobs} closed, {metro.canceled_jobs} cancelled
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right hidden md:block">
                                <p className="text-sm font-medium">
                                  {formatPercentage(cancellationRate)} cancelled
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {metro.total_jobs} total jobs
                                </p>
                              </div>
                              <Badge
                                variant={cancellationRate > 20 ? "destructive" : "outline"}
                                className="md:hidden"
                              >
                                {formatPercentage(cancellationRate)}
                              </Badge>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mt-2 ml-4 space-y-2">
                            {metro.technicians.map(tech => {
                              const techCancellationRate = tech.total_jobs > 0
                                ? (tech.canceled_jobs / tech.total_jobs) * 100
                                : 0;

                              return (
                                <Collapsible key={tech.tech_id}>
                                  <CollapsibleTrigger className="w-full">
                                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                                      <div className="flex items-center gap-3">
                                        {tech.cancelled_jobs_details.length > 0 ? (
                                          <ChevronRight className="h-4 w-4 transition-transform ui-expanded:rotate-90" />
                                        ) : (
                                          <div className="w-4" />
                                        )}
                                        <div className="text-left">
                                          <p className="font-medium">{tech.tech_name}</p>
                                          <p className="text-sm text-muted-foreground">
                                            {tech.closed_jobs} closed, {tech.canceled_jobs} cancelled
                                          </p>
                                        </div>
                                      </div>
                                      <Badge
                                        variant={techCancellationRate > 20 ? "destructive" : "outline"}
                                      >
                                        {formatPercentage(techCancellationRate)}
                                      </Badge>
                                    </div>
                                  </CollapsibleTrigger>
                                  {tech.cancelled_jobs_details.length > 0 && (
                                    <CollapsibleContent>
                                      <div className="mt-2 ml-7 space-y-2">
                                        {tech.cancelled_jobs_details.map(job => (
                                          <div
                                            key={job.id}
                                            className="p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                                            onClick={() => navigate(`/jobs/${job.id}`)}
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                  <p className="font-medium">{job.job_number}</p>
                                                  <Badge variant="destructive" className="text-xs">
                                                    Cancelled
                                                  </Badge>
                                                </div>
                                                <CustomerLink
                                                  customerId={job.customer_id}
                                                  name={job.customer_name}
                                                  className="mt-1 block text-sm text-muted-foreground"
                                                />
                                                {job.scheduled_date && (
                                                  <p className="text-xs text-muted-foreground mt-1">
                                                    Scheduled: {formatDate(new Date(job.scheduled_date), 'PP')}
                                                  </p>
                                                )}
                                              </div>
                                              <div className="text-right">
                                                {job.cancelled_at && (
                                                  <p className="text-xs text-muted-foreground">
                                                    {formatDate(new Date(job.cancelled_at), 'PP')}
                                                  </p>
                                                )}
                                              </div>
                                            </div>
                                            <div className="mt-2 pt-2 border-t">
                                              <p className="text-sm">
                                                <span className="text-muted-foreground">Reason: </span>
                                                <span className="font-medium">
                                                  {job.cancellation_reason || 'Not specified'}
                                                </span>
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </CollapsibleContent>
                                  )}
                                </Collapsible>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
    </div>
  );
};
